/**
 * «حذفِ بخشی از داده‌ها، بدونِ حذفِ حساب» — فهرستِ کارها و حالتِ هر ردیف.
 *
 * ═══ چرا اصلاً وجود دارد ═══
 *
 * فرمِ Data safety گوگل‌پلی جدا از «حذفِ حساب» می‌پرسد آیا کاربر می‌تواند
 * حذفِ **بخشی** از داده‌هایش را درخواست کند. جوابِ «نه» روی کارتِ فروشگاه
 * می‌نشیند («Developer hasn't provided a way to request data deletion»).
 * اپ در عمل این توانایی را داشت — عکس را می‌شد برداشت، شماره را می‌شد پس
 * داد — ولی پراکنده و بی‌نام، پس نه کاربر پیدایش می‌کرد و نه گوگل.
 *
 * این‌جا همان کارهای موجود یک فهرستِ نام‌دار می‌شوند. هیچ‌کدام «درخواست»
 * نیست: دکمه که خورد، داده همان لحظه می‌رود.
 *
 * ═══ چرا این فایل، و نه داخلِ هر دو صفحه ═══
 *
 * همان دلیلِ `contentReport.ts`: فهرست و ترتیبش یک تصمیمِ محصولی است که در
 * دو رابط (صفحه‌ی وب و صفحه‌ی موبایل) دیده می‌شود. اگر هر کدام فهرستِ خودش
 * را بنویسد، اولین موردی که اضافه شود در یکی از دو جا می‌افتد و کسی
 * نمی‌فهمد — و این‌جا «کسی نمی‌فهمد» یعنی صفحه‌ای که به گوگل نشان داده‌ایم
 * با کاری که اپ می‌کند فرق دارد.
 *
 * خودِ پاک کردن در `data/dataEraseRepo.ts` است؛ این فایل هیچ رفت‌وبرگشتی
 * ندارد و برای همین با `npm test` سنجیده می‌شود.
 */

/** هفت چیزی که جدا از حساب می‌شود پاکشان کرد. */
export type ErasableKind =
  | 'avatar' | 'phones' | 'status' | 'contacts'
  | 'signals' | 'events' | 'messages';

/**
 * ترتیبِ نمایش، و عمداً در دو گروه.
 *
 * چهارتای اول فقط مالِ خودِ کاربرند — از چیزی که بیشتر از همه می‌بیندش
 * (عکسِ خودش) تا چیزی که کمتر از همه به یادش هست (دفترچه‌ای که یک بار وارد
 * کرده). برداشتنشان هیچ‌کس دیگری را تکان نمی‌دهد.
 *
 * سه‌تای آخر فرق دارند: **بقیه هم دیده‌اندشان**. یک سیگنال که دوستانت
 * جوابش را داده‌اند، رخدادی که کسی رویش برنامه گذاشته، پیامی وسطِ گفتگوی
 * یک نفرِ دیگر. برای همین قاعده‌شان هم فرق می‌کند (نگاه کن به
 * `AFFECTS_OTHERS`) و آخرِ فهرست‌اند، نه اولش.
 */
export const ERASABLE_KINDS: ErasableKind[] = [
  'avatar', 'phones', 'status', 'contacts',
  'signals', 'events', 'messages',
];

/** چقدر داده از هر نوع مانده. صفر یعنی چیزی برای حذف نیست. */
export type EraseCounts = Record<ErasableKind, number>;

/**
 * حالتِ «هنوز نمی‌دانیم» و حالتِ دمو، هر دو.
 *
 * جدا تعریف شده تا رابط بتواند قبل از رسیدنِ جواب هم ردیف‌ها را بکشد —
 * غیرفعال، ولی دیده‌شونده. صفحه‌ای که با یک اسپینر شروع شود، برای بازبینِ
 * گوگل یعنی صفحه‌ی خالی.
 */
export const NO_ERASABLE_DATA: EraseCounts = {
  avatar: 0, phones: 0, status: 0, contacts: 0, signals: 0, events: 0, messages: 0,
};

/** عنوانِ ردیف — کلیدِ i18n، چون کاربر می‌بیندش. */
export const ERASE_TITLE_KEY: Record<ErasableKind, string> = {
  avatar: 'eraseAvatar',
  phones: 'erasePhones',
  status: 'eraseStatus',
  contacts: 'eraseContacts',
  signals: 'eraseSignals',
  events: 'eraseEvents',
  messages: 'eraseMessages',
};

/** متنِ زیرِ عنوان وقتی داده‌ای هست: «چه چیزی الان ذخیره است». */
export const ERASE_PRESENT_KEY: Record<ErasableKind, string> = {
  avatar: 'eraseAvatarHas',
  phones: 'erasePhonesHas',
  status: 'eraseStatusHas',
  contacts: 'eraseContactsHas',
  signals: 'eraseSignalsHas',
  events: 'eraseEventsHas',
  messages: 'eraseMessagesHas',
};

/** سوالِ تاییدِ همان ردیف. هر کار تاییدِ خودش را می‌گیرد، نه یک تاییدِ کلی. */
export const ERASE_CONFIRM_KEY: Record<ErasableKind, string> = {
  avatar: 'eraseAvatarConfirm',
  phones: 'erasePhonesConfirm',
  status: 'eraseStatusConfirm',
  contacts: 'eraseContactsConfirm',
  signals: 'eraseSignalsConfirm',
  events: 'eraseEventsConfirm',
  messages: 'eraseMessagesConfirm',
};

/**
 * این ردیف چیزی را برمی‌دارد که آدم‌های دیگر هم دیده‌اند.
 *
 * رابط کنارشان یک خطِ هشدار می‌گذارد، چون قاعده‌شان با بقیه فرق دارد و آن
 * فرق باید **قبل** از زدنِ دکمه گفته شود: چیزی که دیگران رویش حساب کرده‌اند
 * بی‌صدا غیب نمی‌شود، «لغو شد» می‌شود.
 */
export const AFFECTS_OTHERS: Record<ErasableKind, boolean> = {
  avatar: false,
  phones: false,
  status: false,
  contacts: false,
  signals: true,
  events: true,
  messages: true,
};

/**
 * آیا عددِ این نوع به کاربر گفته می‌شود.
 *
 * «۳ شماره» اطلاعات است؛ «۱ عکس پروفایل» فقط شمردنِ چیزی که یکی بیشتر
 * نمی‌شود. پس دو نوع جمله داریم و این‌جا مشخص می‌شود کدام کدام است.
 */
const COUNTED: Record<ErasableKind, boolean> = {
  avatar: false,
  phones: true,
  status: false,
  contacts: true,
  signals: true,
  events: true,
  messages: true,
};

/** یک ردیفِ فهرست، آماده‌ی رندر — همان چیزی که هر دو اپ می‌کشند. */
export interface EraseRow {
  kind: ErasableKind;
  /** کلیدِ عنوان. */
  titleKey: string;
  /** کلیدِ متنِ زیرِ عنوان — یا «چه چیزی هست» یا «چیزی نیست». */
  stateKey: string;
  /** پارامترهای i18nِ `stateKey`؛ فقط جایی که عدد معنا دارد. */
  params?: { n: number };
  /** کلیدِ سوالِ تایید. */
  confirmKey: string;
  /** آدم‌های دیگر هم این را دیده‌اند — نگاه کن به `AFFECTS_OTHERS`. */
  affectsOthers: boolean;
  /**
   * چیزی برای حذف نیست.
   *
   * ردیف **غیرفعال** می‌شود و نه پنهان: «چیزی برای حذف نیست» خودش یک
   * جوابِ کامل به «چه چیزی از من دارید» است، و پنهان کردنش همان سوال را
   * بی‌جواب می‌گذارد.
   */
  disabled: boolean;
}

/**
 * فهرستِ ردیف‌ها از روی شمارش‌ها.
 *
 * همه‌ی نوع‌ها همیشه برمی‌گردند — چیدنِ فهرست به داشتن یا نداشتنِ داده
 * وابسته نیست، فقط فعال بودنِ دکمه‌اش.
 */
export function eraseRows(counts: EraseCounts): EraseRow[] {
  return ERASABLE_KINDS.map((kind) => {
    const n = counts[kind] ?? 0;
    const empty = n <= 0;
    return {
      kind,
      titleKey: ERASE_TITLE_KEY[kind],
      stateKey: empty ? 'eraseNothing' : ERASE_PRESENT_KEY[kind],
      ...(!empty && COUNTED[kind] ? { params: { n } } : {}),
      confirmKey: ERASE_CONFIRM_KEY[kind],
      affectsOthers: AFFECTS_OTHERS[kind],
      disabled: empty,
    };
  });
}

/** آیا اصلاً چیزی برای حذف هست — برای یادداشتِ بالای فهرست. */
export const hasAnythingToErase = (counts: EraseCounts): boolean =>
  ERASABLE_KINDS.some((k) => (counts[k] ?? 0) > 0);
